/**
 * Checksum Validation Utilities
 *
 * DRY-extracted for shared use between backend-wms and frontend.
 * Provides multiple checksum algorithms for identifier validation.
 *
 * Algorithms:
 * - LUHN: Credit card, IMEI validation
 * - MOD10: EAN/UPC barcode check digit (weighted 3/1)
 * - MOD43: Code 39 extended check character
 *
 * Rule 15 Compliance: Single source of truth for all checksum logic.
 */

import { ChecksumAlgorithm } from '../types/wms/identifier-validation.types';

/**
 * Validate a value using the specified checksum algorithm.
 *
 * @param value - The string value to validate
 * @param algorithm - The checksum algorithm to apply
 * @returns true if checksum is valid (or algorithm is NONE)
 */
export function validateChecksum(value: string, algorithm: ChecksumAlgorithm): boolean {
  switch (algorithm) {
    case ChecksumAlgorithm.NONE:
      return true;
    case ChecksumAlgorithm.LUHN:
      return luhn(value);
    case ChecksumAlgorithm.MOD10:
      return mod10(value);
    case ChecksumAlgorithm.MOD43:
      return mod43(value);
    default:
      return true;
  }
}

/**
 * Calculate MOD-10 check digit for a numeric string.
 * Used by barcode generation (EAN, UPC, ITF-14).
 */
export function calculateMod10CheckDigit(value: string): number {
  const digits = value.split('').map(Number);
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    sum += digits[i] * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * LUHN algorithm -- used for IMEI, credit card validation.
 * Expects the check digit to be the last character.
 */
function luhn(value: string): boolean {
  if (!/^\d+$/.test(value)) {
    return false;
  }

  let sum = 0;
  let alt = false;
  for (let i = value.length - 1; i >= 0; i--) {
    let n = parseInt(value[i], 10);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/**
 * MOD-10 (EAN/UPC weighted checksum) -- used for EAN-13, EAN-8, UPC-A, ITF-14.
 * Weights alternate between 3 and 1 starting from the first digit.
 * The last digit is the check digit.
 */
function mod10(value: string): boolean {
  if (!/^\d+$/.test(value)) {
    return false;
  }

  const digits = value.split('').map(Number);
  const checkDigit = digits.pop();
  if (checkDigit === undefined) {
    return false;
  }

  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    sum += digits[i] * (i % 2 === 0 ? 3 : 1);
  }

  const calculatedCheck = (10 - (sum % 10)) % 10;
  return calculatedCheck === checkDigit;
}

/**
 * MOD-43 -- used for Code 39 check character validation.
 * Character set: 0-9, A-Z, -, ., space, $, /, +, %
 * The last character is the check character.
 */
function mod43(value: string): boolean {
  const MOD43_CHARSET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-. $/+%';

  if (value.length < 2) {
    return false;
  }

  const dataChars = value.slice(0, -1);
  const checkChar = value[value.length - 1];

  let sum = 0;
  for (const ch of dataChars) {
    const idx = MOD43_CHARSET.indexOf(ch);
    if (idx === -1) {
      return false;
    }
    sum += idx;
  }

  const expectedIdx = sum % 43;
  return MOD43_CHARSET[expectedIdx] === checkChar;
}
